import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import AddIcon from "@mui/icons-material/Add"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from "@mui/material"
import { useState } from "react"
import { Dialog } from "src/shared/components"
import { useDeletePrinterConfigMutation } from "src/graphql/quark/quark.type"
import { toast } from "react-toastify"
import type { PrinterConfigApi } from "./printer-config-mappers"
import { ChipStyled } from "src/shared/components"

interface ManagePrinterConfigsDialogProps {
  open: boolean
  onClose: () => void
  configs: PrinterConfigApi[]
  loading?: boolean
  onRefetch: () => void
  onUse: (config: PrinterConfigApi) => void
  onEdit: (config: PrinterConfigApi) => void
  onAddNew: () => void
}

export default function ManagePrinterConfigsDialog({
  open,
  onClose,
  configs,
  loading,
  onRefetch,
  onUse,
  onEdit,
  onAddNew
}: ManagePrinterConfigsDialogProps) {
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deleteMutation, { loading: deleteLoading }] = useDeletePrinterConfigMutation({
    onCompleted: () => {
      toast.success("Printer config deleted.")
      setDeleteId(null)
      setConfirmDeleteId(null)
      onRefetch()
    },
    onError: (e) => {
      toast.error(e.message || "Failed to delete.")
      setDeleteId(null)
      setConfirmDeleteId(null)
    }
  })

  const handleDeleteClick = (id: string) => {
    setConfirmDeleteId(id)
  }

  const handleDeleteConfirm = () => {
    if (confirmDeleteId) {
      setDeleteId(confirmDeleteId)
      deleteMutation({ variables: { deletePrinterConfigId: confirmDeleteId } })
    }
  }

  const handleDeleteCancel = () => {
    setConfirmDeleteId(null)
  }

  const handleClose = () => {
    if (!deleteLoading) {
      setDeleteId(null)
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      toggleDialog={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ m: 0, p: 3, pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" component="h2">
            Printer configs
          </Typography>
          <IconButton aria-label="close" onClick={handleClose} sx={{ color: (t) => t.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Save and reuse label layouts. Select one to use on this page or add a new config from your current
          settings.
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : configs.length === 0 ? (
          <Box py={2} textAlign="center" color="text.secondary">
            <Typography>No printer configs yet.</Typography>
            <Typography variant="body2">Add one using your current label settings.</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {configs.map((config) => (
              <ListItem
                key={config.id}
                sx={{
                  border: (t) => `1px solid ${t.palette.divider}`,
                  borderRadius: 2,
                  mb: 1,
                  bgcolor: "background.paper"
                }}
                secondaryAction={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={() => {
                        onUse(config)
                        handleClose()
                      }}
                    >
                      Use
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => {
                        onEdit(config)
                        onClose()
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={
                        deleteId === config.id ? (
                          <CircularProgress size={14} />
                        ) : (
                          <DeleteOutlineIcon />
                        )
                      }
                      disabled={deleteId !== null}
                      onClick={() => handleDeleteClick(config.id)}
                    >
                      Delete
                    </Button>
                  </Box>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography fontWeight={600}>{config.name}</Typography>
                      {config.isDefault && (
                        <ChipStyled label="Default" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                  }
                  secondary={`${config.labelWidth}×${config.labelHeight} mm · ${config.columns} col(s)`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" color="primary" onClick={onAddNew}>
          Add new from current settings
        </Button>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
      </DialogActions>

      {/* Delete confirmation */}
      <Dialog
        open={!!confirmDeleteId}
        toggleDialog={deleteLoading ? () => {} : handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>Delete printer config?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This cannot be undone. Continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleDeleteCancel} color="inherit" disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteLoading}
            onClick={handleDeleteConfirm}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
